import React from 'react'
import { baseUrl } from '@fs/zion-config'
import { i18n } from '@fs/zion-locale'
import { useTranslation } from 'react-i18next'
import Head, { PageHead } from '@fs/zion-head'
import { useOverlay, DialogOverlay, DialogOverlayActions, Button, Paragraph, Separator } from '@fs/zion-ui'
import { usePageView } from '@fs/zion-analytics'
import { Usabilla } from '@fs/zion-usabilla'
import { css } from '@emotion/core'
import { useFeatureFlag } from '@fs/zion-flags'
import oldEnglandMapsImage from './OldEnglandMaps.webp'
import EnglandMapsHelpTutorial from '../components/EnglandMapsHelpTutorial/EnglandMapsHelpTutorial'

const fullscreenContainerStyle = css`
  position: fixed;
  top: 0;
  inset-inline-start: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`

const fullscreenImageStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (max-width: 768px) {
    object-position: center;
  }
`

type EnglandMapsAppPageHeadProps = Readonly<{
  title: string
  description: string
  canonicalUrl: string
}>

function EnglandMapsAppPageHead({ title, description, canonicalUrl }: EnglandMapsAppPageHeadProps): JSX.Element {
  return (
    <>
      <PageHead includeHreflang title={title} description={description} url={canonicalUrl} />
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
    </>
  )
}

function ShowEnglandJurisdictions(): JSX.Element {
  const [t] = useTranslation()

  // Feature flag check for England Maps Help Tutorial
  const showEnglandMapsHelpTutorial = useFeatureFlag('standards_places_englandMapsHelpTutorial')?.isOn
  const showNextLink = useFeatureFlag('standards_places_englandMapsNEXT')?.isOn

  // Determine which ArcGIS experience URL to use based on baseUrl
  let isBetaHost = false
  try {
    isBetaHost = new URL(baseUrl).hostname === 'beta.familysearch.org'
  } catch {
    isBetaHost = false
  }
  const arcgisUrl =
    isBetaHost || showNextLink
      ? 'https://experience.arcgis.com/experience/ecfbb152f70546389f8b6d88dbd5b051'
      : 'https://experience.arcgis.com/experience/a0ca526b8be34c408f23d0f5fd99dfda'

  return (
    <div id="mainSearch">
      <Usabilla side="right" url="https://w.usabilla.com/5c534b30ae2a.js" />
      {showEnglandMapsHelpTutorial && <EnglandMapsHelpTutorial />}
      <EnglandMapsAppPageHead
        title={t('england-maps.page.title', 'England Maps')}
        description={t('england-maps-page.page.description', 'FamilySearch England Maps')}
        canonicalUrl={`${baseUrl}/${i18n.language}/standards/england-maps/`}
      />
      {/* referrerPolicy keeps the authenticated /mapp path out of the Referer sent to the third-party host */}
      <iframe
        data-testid="england-maps-iframe"
        src={arcgisUrl}
        title="England Maps"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        style={{ display: 'initial', border: 'none', height: 'calc(100vh - 62px)', width: '100%' }}
      />
    </div>
  )
}

export default function EnglandMapsPage(): JSX.Element {
  const showPopup = useFeatureFlag('standards_places_showPopup')?.isOn
  const overlay = useOverlay()
  const initData = { page_detail: 'FamilySearch England Maps' }
  usePageView(initData)

  if (!showPopup) {
    return <ShowEnglandJurisdictions />
  }

  return (
    <>
      <div
        id="mainSearch"
        css={fullscreenContainerStyle}
        onClick={overlay.handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            ;(overlay.handleClick as unknown as (event: React.KeyboardEvent) => void)(e)
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Open dialog"
      >
        <img src={oldEnglandMapsImage} alt="Old England Maps" css={fullscreenImageStyle} />
      </div>

      <DialogOverlay size="md" title="Update" {...overlay}>
        <Paragraph size="sm">
          {`England Jurisdictions 1851 is temporarily offline
          for system upgrades. We're enhancing the experience to better serve your
          research needs. The service will be restored as soon as possible, and we'll
          provide updates on this page as work progresses.
          We appreciate your patience during this maintenance period.`}
        </Paragraph>
        <Separator size="md" />
        <DialogOverlayActions>
          <Button emphasis="high" onClick={overlay.close}>
            Close
          </Button>
        </DialogOverlayActions>
      </DialogOverlay>
    </>
  )
}
